/** @jsxImportSource @emotion/react */
import { useEffect, useState, useCallback } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import { useMobxStore } from 'src/state'
import { MENU_HEIGHT } from '../../css'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { ROUTES } from '@sportninja/common/constants/app'
import AccountSettings from 'src/components/AccountSettings'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import UpgradeAccountModal from 'src/components/ts-components/UpgradeAccountModal'
import Text from 'src/components/ts-components/Text'
import NotificationSettingsModal from 'src/components/NotificationSettingsModal'
import NotificationSettingsGearIcon from 'src/components/icons/NotificationSettingsGearIcon'
import UserSettingsIcon from 'src/components/icons/UserSettingsIcon'
import ProfileIcon from 'src/components/icons/ProfileIcon'
import AccountSettingsIcon from 'src/components/icons/AccountSettingsIcon'
import SubscriptionIcon from 'src/components/icons/SubscriptionIcon'
import PrivacyPolicyIcon from 'src/components/icons/PrivacyPolicyIcon'
import LogoutIcon from 'src/components/icons/LogoutIcon'
import ProfileMenuItem from './components/ProfileMenuItem'

const Separator = () => (
  <div
    css={css`
      width: 100%;
      height: 1px;
      background-color: ${colors.SECONDARY_100};
    `}
  />
)

interface Props {
  onLogoutClick: () => void
  user: any
  isMobile: boolean
}

export const Profile = observer(
  ({ onLogoutClick, user, isMobile = false }: Props) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
    const {
      me: {
        state: { me },
        clearUser,
        fetchMe,
      },
    } = useMobxStore()

    const history = useHistory()

    const handleCreateLeagueAccount = useCallback(() => {
      setIsUpgradeModalOpen(true)
      setIsOpen(false) // Close the profile dropdown
    }, [])

    const confirmExit = () => {
      if (window.confirm('Are you sure you want to logout?')) {
        clearUser()
        onLogoutClick()
      }
    }

    useEffect(() => {
      if (!me?.id) {
        fetchMe()
      }
    }, [me?.id, fetchMe])

    return (
      <>
        <div
          onClick={() => setIsOpen(!isOpen)}
          css={css`
            cursor: pointer;
            min-height: 40px;
            max-height: 40px;
            min-width: 40px;
            max-width: 40px;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            border: 2px solid ${colors.ATTENDANCE_GRAY};
            transition: all 0.2s ease-in-out;
            &:hover {
              border: 2px solid ${colors.DEFAULT_FLAIR};
            }
            ${isMobile
              ? css`
                  min-height: 32px;
                  max-height: 32px;
                  min-width: 32px;
                  max-width: 32px;
                `
              : null}
          `}
        >
          {me?.image?.id ? (
            <div>
              <img
                alt={me?.name_first}
                src={getImageThumbnailId(me)}
                css={css`
                  width: 100%;
                  height: 100%;
                `}
              />
            </div>
          ) : (
            <div>
              <p
                css={css`
                  font-size: 0.9rem;
                  text-transform: uppercase;
                `}
              >
                {me?.name_first && me?.name_last
                  ? me?.name_first[0] + me?.name_last[0]
                  : '?'}
              </p>
            </div>
          )}
        </div>
        <div
          onClick={() => setIsOpen(false)}
          css={css`
            position: fixed;
            bottom: 0;
            right: 0;
            width: 100%;
            height: 100%;
            z-index: ${isOpen ? '1000' : '100'};
            pointer-events: ${isOpen ? 'all' : 'none'};
            transition: all 0.2s ease-in-out;
            display: ${isOpen ? 'flex' : 'none'};
          `}
        >
          <div
            css={css`
              position: absolute;
              top: ${MENU_HEIGHT + 8}px;
              right: 8px;
              z-index: ${isOpen ? '1001' : '101'};
              opacity: ${isOpen ? 1 : 0};
              display: ${isOpen ? 'flex' : 'none'};
              width: 250px;
              overflow: hidden;
              white-space: nowrap;
              transition: all 0.2s ease-in-out;
              background-color: ${'#293241'};
              padding: 16px;
              border-radius: 8px;
              flex-direction: column;
              gap: 16px;
              border: 1px solid ${colors.SECONDARY_100};
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <div
                css={css`
                  min-height: 40px;
                  max-height: 40px;
                  min-width: 40px;
                  max-width: 40px;
                  border-radius: 50%;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  overflow: hidden;
                  border: 1px solid ${colors.NEUTRAL_0};
                  background-color: ${colors.NEUTRAL_30};
                `}
              >
                {me?.image?.id ? (
                  <div>
                    <img
                      alt={me?.name_first}
                      src={getImageThumbnailId(me)}
                      css={css`
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                      `}
                    />
                  </div>
                ) : (
                  <div>
                    <img
                      alt={me?.name_first}
                      src={
                        '/images/generic_placeholders/placeholder_player.png'
                      }
                      css={css`
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                      `}
                    />
                  </div>
                )}
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                  > p {
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    overflow: hidden;
                  }
                `}
              >
                <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
                  {`${user?.nameFirst}  ${user?.nameLast}`}
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  {user?.email}
                </Text>
              </div>
            </div>
            <Separator />
            <ProfileMenuItem
              icon={<ProfileIcon size={24} color={colors.PRIMARY} />}
              label='Profile'
              onClick={() => history.push(ROUTES.PROFILE.ROOT)}
            />
            <AccountSettings
              button={({ onClick }) => (
                <ProfileMenuItem
                  icon={<UserSettingsIcon size={24} color={colors.PRIMARY} />}
                  label='User Settings'
                  onClick={onClick}
                />
              )}
            />
            <ProfileMenuItem
              icon={
                <NotificationSettingsGearIcon
                  size={24}
                  color={colors.PRIMARY}
                />
              }
              label='Notification Settings'
              onClick={() => setIsSettingsModalOpen(true)}
            />
            {me?.account_id && me?.account_type && me?.account_type > 1 ? (
              <>
                <ProfileMenuItem
                  icon={
                    <AccountSettingsIcon size={24} color={colors.PRIMARY} />
                  }
                  label='Account Settings'
                  onClick={() => history.push(ROUTES.GENERAL_SETTINGS)}
                />
                {!isCanlan ? (
                  <ProfileMenuItem
                    icon={<SubscriptionIcon size={24} color={colors.PRIMARY} />}
                    label='Subscription'
                    onClick={() =>
                      history.push(
                        ROUTES.GENERAL_SETTINGS + '?origin=subscription'
                      )
                    }
                  />
                ) : null}
              </>
            ) : null}
            {(!me?.account_id || me?.account_type === 1) && !isCanlan ? (
              <>
                <Separator />
                <ProfileMenuItem
                  icon={
                    <AccountSettingsIcon size={24} color={colors.PRIMARY} />
                  }
                  label='Upgrade to League Account'
                  onClick={handleCreateLeagueAccount}
                  textColor={colors.PRIMARY}
                />
              </>
            ) : null}
            <Separator />
            <a
              href='/docs/privacy.pdf'
              target='_blank'
              rel='noopener noreferrer'
            >
              <ProfileMenuItem
                icon={<PrivacyPolicyIcon size={24} color={colors.PRIMARY} />}
                label='Privacy Policy'
              />
            </a>
            <ProfileMenuItem
              icon={<LogoutIcon size={24} color={colors.ERROR_LIGHT} />}
              label='Log Out'
              onClick={confirmExit}
              textColor={colors.ERROR_LIGHT}
            />
          </div>
        </div>

        {/* Upgrade Account Modal */}
        <UpgradeAccountModal
          email={me?.email || ''}
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          onComplete={() => {
            fetchMe()
          }}
          fetchMe={fetchMe}
        />

        {/* Notification Settings Modal */}
        <NotificationSettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
        />
      </>
    )
  }
)
